import React from 'react';
import Icon from '../../../components/AppIcon';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const PerformanceGraphsPanel = ({ performanceMetrics }) => {
  // Mock time-series data for visualization
  const generateTimeSeriesData = () => {
    const data = [];
    const now = Date.now();
    for (let i = 11; i >= 0; i--) {
      data?.push({
        time: new Date(now - i * 5 * 60 * 1000)?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        responseTime: Math.floor(100 + Math.random() * 100),
        errorRate: Math.random() * 2,
        throughput: Math.floor(1000 + Math.random() * 500)
      });
    }
    return data;
  };

  const timeSeriesData = generateTimeSeriesData();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Icon name="TrendingUp" className="w-5 h-5 text-blue-600" />
          Real-time Performance Graphs
        </h3>
        <p className="text-sm text-gray-600 mt-1">Live performance metrics and trend analysis</p>
      </div>

      {/* Response Time Graph */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-1">API Response Time</h4>
          <p className="text-xs text-gray-600">Average response time over last hour</p>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={timeSeriesData}>
            <defs>
              <linearGradient id="colorResponseTime" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="time" tick={{ fontSize: 12 }} stroke="#6b7280" />
            <YAxis tick={{ fontSize: 12 }} stroke="#6b7280" />
            <Tooltip
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
            />
            <Area
              type="monotone"
              dataKey="responseTime"
              stroke="#3b82f6"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorResponseTime)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Error Rate Graph */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-1">Error Rate</h4>
          <p className="text-xs text-gray-600">Error percentage over last hour</p>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={timeSeriesData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="time" tick={{ fontSize: 12 }} stroke="#6b7280" />
            <YAxis tick={{ fontSize: 12 }} stroke="#6b7280" />
            <Tooltip
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
            />
            <Line
              type="monotone"
              dataKey="errorRate"
              stroke="#ef4444"
              strokeWidth={2}
              dot={{ fill: '#ef4444', r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Throughput Graph */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-1">Request Throughput</h4>
          <p className="text-xs text-gray-600">Requests per minute over last hour</p>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={timeSeriesData}>
            <defs>
              <linearGradient id="colorThroughput" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="time" tick={{ fontSize: 12 }} stroke="#6b7280" />
            <YAxis tick={{ fontSize: 12 }} stroke="#6b7280" />
            <Tooltip
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
            />
            <Area
              type="monotone"
              dataKey="throughput"
              stroke="#10b981"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorThroughput)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Current Metrics Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div className="text-xs text-blue-600 mb-1">Current Response Time</div>
          <div className="text-2xl font-bold text-blue-900">
            {performanceMetrics?.api?.avgResponseTime || '--'}ms
          </div>
        </div>
        <div className="bg-red-50 rounded-lg p-4 border border-red-200">
          <div className="text-xs text-red-600 mb-1">Current Error Rate</div>
          <div className="text-2xl font-bold text-red-900">
            {performanceMetrics?.api?.errorRate || '--'}%
          </div>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <div className="text-xs text-green-600 mb-1">Requests/min</div>
          <div className="text-2xl font-bold text-green-900">
            {performanceMetrics?.api?.requestsPerMinute?.toLocaleString() || '--'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceGraphsPanel;
